// The phone's brain. One long-lived ngin Query boots the connection (guest /
// offerer), wires mic + TTS, drives conversation mode, and `notify`s a single
// UI state snapshot the composition renders via dodo `watch`.
//
// User intents arrive as dispatched Actions that emit on the engineFeed; the
// query (which holds the live peer/mic/tts) reacts. This is ngin's choreographer
// pattern: Actions stay thin verbs, the Query owns coordination.

import { Query, Action } from '@3sln/ngin';
import {
  LINK,
  COMMAND,
  NOTICE_CODE,
  helloGuest,
  text as mkText,
  command as mkCommand,
  listSessions as mkListSessions,
  connectSession as mkConnectSession,
  askReply as mkAskReply,
  formReply as mkFormReply,
  formFileBegin as mkFormFileBegin,
  formFileEnd as mkFormFileEnd,
  attachBegin as mkAttachBegin,
  attachEnd as mkAttachEnd,
  ping as mkPing,
  pong as mkPong,
} from '@bridle/protocol/link';
import { offer as mkOffer } from '@bridle/protocol/signaling';
import { parse as parseCommand, CMD } from './commands.js';
import { meaningfulTranscript } from '../lib/transcript.js';
import { createEarcons, createWakeLock, setupMediaSession } from '../hands.js';

export const CONNECTION = Object.freeze({
  NONE: 'no-tether', // no tether selected — scan a QR
  CONNECTING: 'connecting',
  WAITING: 'waiting', // signaling up, desktop not present yet
  NEGOTIATING: 'negotiating', // channel opening, or open and awaiting the host's verdict
  TETHERED: 'tethered',
  RECONNECTING: 'reconnecting',
  UNPAIRED: 'unpaired', // the desktop refused this device — terminal until unpaired
  ERROR: 'error',
});

const SPEAK_FLUSH_MS = 800;
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
const lastBoundary = (s) => {
  const m = s.match(/[\s\S]*[.!?\n]/);
  return m ? m[0].length : 0;
};

const ORDINAL = { one: 1, two: 2, three: 3, four: 4, five: 5, first: 1, second: 2, third: 3, fourth: 4, fifth: 5 };
// Map a spoken answer onto one of the offered choices (exact / contains / ordinal).
function resolveChoice(text, choices) {
  const t = (text || '').trim();
  if (!choices || !choices.length) return t;
  const low = t.toLowerCase().replace(/[.,!?]$/g, '');
  const exact = choices.find((c) => c.toLowerCase() === low);
  if (exact) return exact;
  const part = choices.find((c) => low.includes(c.toLowerCase()) || c.toLowerCase().includes(low));
  if (part) return part;
  const n = /^\d+$/.test(low) ? parseInt(low, 10) : ORDINAL[low];
  if (n && choices[n - 1]) return choices[n - 1];
  return t;
}

export class TetherQuery extends Query {
  static deps = ['config', 'settings', 'signaling', 'peer', 'mic', 'tts', 'stt', 'tethers', 'identity'];

  async boot({ config, settings, signaling, peer: makePeer, mic, tts, stt, tethers, identity }, { notify, engineFeed }) {
    const state = {
      connection: CONNECTION.NONE,
      conversation: false,
      listening: false,
      speaking: false,
      processing: false, // transcribing an utterance on-device
      awaitingReply: false, // sent input; the agent is working on this turn
      attachments: [], // { id, name, size, file } staged to send with the next message
      sttState: 'idle', // idle | loading | ready | error
      sttProgress: null, // 0..100 when totals are known; null = indeterminate
      sttBytes: 0, // bytes downloaded so far (always known, monotonic)
      preparingVoice: false, // gating hands-free on the one-time model download
      messages: [],
      level: 0,
      error: null,
      room: '',
      agent: null,
      micSupported: !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia),
      ttsSupported: tts.supported,
      voices: tts.voices().map((v) => v.name),
      settings: settings.all(),
      sheetOpen: false,
      sessions: [], // resumable agent sessions (when listed)
      currentSession: null, // { id, title }
      sessionsOpen: false,
      sessionsLoading: false, // awaiting the desktop's session list
      statusLine: '', // transient agent status (set_status)
      toast: null, // { text, level } notice
      ask: null, // { id, question, choices } pending prompt
      form: null, // { id, html, title, submit } agent-rendered form
      tethers: [], // known desktops/agents this phone can reach
      activeTetherId: null,
      tetherLabel: null,
      tethersOpen: false,
      detailsOpen: false, // the connection-details sheet (opened from the status bead)
      shortcutsOpen: false, // keyboard-shortcuts help (desktop)
    };

    // Hands-free helpers (driving / eyes-off use).
    const earcons = createEarcons();
    const wake = createWakeLock();
    const earcon = (name) => settings.get('earcons') && earcons[name] && earcons[name]();
    const refreshSettings = () => {
      state.settings = settings.all();
    };
    const nonce = Math.random().toString(36).slice(2, 8);
    const notifyNow = () => notify({ ...state, messages: state.messages.slice() });
    const push = (patch) => {
      Object.assign(state, patch);
      notifyNow();
    };

    let peer = null;
    let speakBuffer = '';
    let incomingAsset = null; // asset being received (between ASSET_BEGIN/END)
    let flushTimer = null;
    let lastLevelNotify = 0;

    // --- turn tracking + delivery ------------------------------------------
    // A turn = from when we send input until the agent finishes replying. The
    // desktop marks it precisely with STATUS turn-start/turn-end; for agents that
    // don't, we fall back to "no output for TURN_IDLE_MS", with a hard cap so a
    // silent turn can't wedge things.
    //
    // Every user message carries a `delivery` the UI shows honestly:
    //   pending — not on the wire yet (no live tether, or held for later)
    //   sent    — handed to the open, verified data channel
    //   read    — the agent picked it up (turn started / began replying)
    // Two holding buffers keep messages from being lost or interleaved:
    //   outbox   — composed while not linked; flushed when the tether is ready
    //   outQueue — composed mid-turn; sent as one combined message at turn end
    const TURN_IDLE_MS = 2000;
    const TURN_MAX_MS = 120000;
    let explicitTurn = false; // desktop is sending turn-start/turn-end this turn
    let sawOutput = false; // first output chunk of the current turn arrived
    let idleTimer = null;
    let safetyTimer = null;
    const outQueue = []; // message objects held while the agent is busy
    const outbox = []; // message objects composed while not linked

    // Ready to actually deliver: a live peer AND a verified link (post-HELLO).
    const canDeliver = () => !!peer && state.connection === CONNECTION.TETHERED;

    function beginTurn() {
      explicitTurn = false;
      sawOutput = false;
      clearTimeout(safetyTimer);
      safetyTimer = setTimeout(endTurn, TURN_MAX_MS);
      push({ awaitingReply: true });
    }
    function armIdle() {
      if (explicitTurn) return; // the desktop will tell us when the turn ends
      clearTimeout(idleTimer);
      idleTimer = setTimeout(endTurn, TURN_IDLE_MS);
    }
    function endTurn() {
      clearTimeout(idleTimer);
      clearTimeout(safetyTimer);
      idleTimer = safetyTimer = null;
      explicitTurn = false;
      markAssistantDone();
      flushSpeak();
      // Anything held during the turn goes out now as one combined message; its
      // bubbles were already shown (queued) and just lose the badge.
      const combined = flushQueue();
      if (combined) {
        peer?.send(mkText(combined.text, combined.source));
        beginTurn();
      } else {
        push({ awaitingReply: false });
      }
    }
    function resetTurn() {
      clearTimeout(idleTimer);
      clearTimeout(safetyTimer);
      idleTimer = safetyTimer = null;
      explicitTurn = false;
      // Whatever was queued for this turn is no longer on a live link — move it to
      // the outbox (pending) so it resends when we're linked again, not lost.
      for (const m of outQueue.splice(0)) {
        m.queued = false;
        m.delivery = 'pending';
        outbox.push(m);
      }
      push({ awaitingReply: false });
    }

    // Send now if we can and the agent is idle; otherwise hold it (pending) in the
    // right buffer and reflect that on the message. `source` ('voice'|'text') rides
    // along so the desktop can tag the line for the agent.
    function queueOrSend(text, kind, source = 'text') {
      if (!canDeliver()) {
        outbox.push(addMessage('user', text, kind, { delivery: 'pending', source }));
      } else if (state.awaitingReply) {
        outQueue.push(addMessage('user', text, kind, { delivery: 'pending', queued: true, source }));
      } else {
        addMessage('user', text, kind, { delivery: 'sent', source });
        peer?.send(mkText(text, source));
        beginTurn();
      }
    }
    // The tether just became ready — send anything composed while it wasn't.
    function flushOutbox() {
      if (!outbox.length || !canDeliver()) return;
      for (const m of outbox.splice(0)) {
        m.queued = false;
        if (state.awaitingReply) {
          m.delivery = 'pending';
          m.queued = true;
          outQueue.push(m);
        } else {
          m.delivery = 'sent';
          peer?.send(mkText(m.content, m.source));
          beginTurn();
        }
      }
      notifyNow();
    }
    // The agent picked up our input: promote delivered messages to "read".
    function markRead() {
      let changed = false;
      for (const m of state.messages) {
        if (m.role === 'user' && m.delivery === 'sent') {
          m.delivery = 'read';
          changed = true;
        }
      }
      if (changed) notifyNow();
    }
    function flushQueue() {
      if (!outQueue.length) return null;
      const combined = outQueue.map((q) => q.content).join('\n');
      // If any held message was spoken, treat the batch as voice.
      const source = outQueue.some((m) => m.source === 'voice') ? 'voice' : 'text';
      outQueue.forEach((m) => { m.queued = false; m.delivery = 'sent'; });
      outQueue.length = 0;
      return { text: combined, source };
    }

    // Stream staged attachments to the desktop (which saves them to a folder and
    // references the paths to the agent on the next message). Ordered before the
    // text send, so the desktop has them pending when the message lands.
    // (UPLOAD_CHUNK is defined once below, shared with form-file uploads.)
    let attachSeq = 0;
    async function uploadAttachments() {
      const staged = state.attachments.slice();
      if (!staged.length) return;
      for (const a of staged) {
        peer?.send(mkAttachBegin(a.id, { name: a.name, mime: a.file.type, size: a.size }));
        const buf = new Uint8Array(await a.file.arrayBuffer());
        for (let o = 0; o < buf.length; o += UPLOAD_CHUNK) {
          peer?.sendBinary(buf.subarray(o, o + UPLOAD_CHUNK));
        }
        peer?.send(mkAttachEnd(a.id));
      }
      push({ attachments: [] });
    }

    // --- messages -----------------------------------------------------------
    const addMessage = (role, content, kind, extra) => {
      if (role === 'user' && !extra?.queued) markAssistantDone();
      const msg = { id: `${nonce}-${state.messages.length}`, role, content, kind, ts: Date.now(), ...extra };
      state.messages.push(msg);
      notifyNow();
      return msg;
    };
    const appendAssistant = (chunk) => {
      const last = state.messages[state.messages.length - 1];
      if (last && last.role === 'assistant' && !last.done) {
        last.content += chunk;
      } else {
        state.messages.push({ id: `${nonce}-${state.messages.length}`, role: 'assistant', content: chunk, ts: Date.now() });
      }
      notifyNow();
    };
    const markAssistantDone = () => {
      const last = state.messages[state.messages.length - 1];
      if (last && last.role === 'assistant') last.done = true;
    };

    // Replies (and questions/forms) are read aloud automatically ONLY in hands-free
    // conversation mode; in text mode nothing auto-speaks — the user taps a message's
    // speaker to play it. `autoSpeak` is the user's on/off toggle within that.
    const autoSpeakOn = () => state.conversation && settings.get('autoSpeak');

    // --- streaming TTS: speak whole sentences as output arrives --------------
    const speakChunk = (chunk) => {
      if (!autoSpeakOn()) return;
      speakBuffer += chunk;
      const idx = lastBoundary(speakBuffer);
      if (idx > 0) {
        tts.speak(speakBuffer.slice(0, idx));
        speakBuffer = speakBuffer.slice(idx);
      }
      clearTimeout(flushTimer);
      flushTimer = setTimeout(flushSpeak, SPEAK_FLUSH_MS);
    };
    const flushSpeak = () => {
      clearTimeout(flushTimer);
      if (autoSpeakOn() && speakBuffer.trim()) tts.speak(speakBuffer);
      speakBuffer = '';
    };

    // --- TTS <-> mic (barge-in: don't record ourselves while speaking) ------
    tts.addEventListener('speaking', () => {
      push({ speaking: true });
      if (state.conversation && state.listening) mic.pause();
    });
    tts.addEventListener('idle', () => {
      push({ speaking: false });
      if (state.conversation && state.listening) mic.resume();
    });

    // --- mic ----------------------------------------------------------------
    mic.addEventListener('level', (e) => {
      state.level = e.detail.level;
      const now = performance.now();
      if (now - lastLevelNotify > 120) {
        lastLevelNotify = now;
        notifyNow();
      }
    });
    mic.addEventListener('utterance', (e) => transcribeUtterance(e.detail));

    // Offline STT model load progress -> UI (one-time download). The worker
    // already aggregates per-file bytes into one number; clamp it monotonic here
    // so a late-arriving file can never make the bar jump backwards.
    stt.addEventListener('progress', (e) => {
      const d = e.detail || {};
      if (d.status === 'fallback') return;
      const patch = { sttState: 'loading' };
      if (typeof d.progress === 'number') patch.sttProgress = Math.max(state.sttProgress || 0, Math.round(d.progress));
      if (typeof d.loaded === 'number') patch.sttBytes = Math.max(state.sttBytes || 0, d.loaded);
      push(patch);
    });
    stt.addEventListener('ready', () => push({ sttState: 'ready', sttProgress: 100 }));
    // A model-load failure (e.g. the one-time CDN download) otherwise dies in the
    // worker with the loading banner stuck — surface it and stop conversation.
    stt.addEventListener('error', (e) => {
      earcon('error');
      push({ sttState: 'error', preparingVoice: false, error: `couldn't load the speech model: ${e.detail?.message || 'fetch failed'}` });
      if (state.conversation) stopConversation();
    });

    // Each VAD segment is a complete utterance (Silero already cut it at real
    // speech start/end, ignoring noise). Transcribe it and send — the noise
    // filter is a light safety net for the rare hallucination.
    async function transcribeUtterance({ samples }) {
      push({ processing: true });
      earcon('think');
      try {
        const heard = await stt.transcribeSamples(samples);
        push({ processing: false, sttState: 'ready' });
        onTranscript(heard);
      } catch (err) {
        earcon('error');
        push({ processing: false, sttState: 'error', error: `speech: ${err.message}` });
      }
    }

    // --- connection / negotiation (guest = offerer) -------------------------
    // The host verified us and opened the tether (LINK.READY). Everything that
    // means "we can actually deliver a message" hangs off this, not off the
    // channel opening or off HELLO — HELLO is only the host's challenge, and a
    // device about to be refused receives one just the same.
    let admitted = false;

    function teardownPeer() {
      admitted = false;
      for (const settle of pongWaiters.values()) settle();
      pongWaiters.clear();
      resetTurn();
      if (peer) {
        try {
          peer.close();
        } catch {
          /* noop */
        }
        peer = null;
      }
    }
    async function startOffer() {
      teardownPeer();
      push({ connection: CONNECTION.NEGOTIATING });
      // Mint a fresh peer with ICE servers from the active tether's backend, so
      // a short-lived Cloudflare TURN credential (if any) is scoped per session.
      const t = tethers.active();
      peer = await makePeer({ backendUrl: t?.backendUrl, room: t?.room });
      peer.addEventListener('open', onPeerOpen);
      peer.addEventListener('message', (e) => handleLink(e.detail.msg));
      peer.addEventListener('binary', (e) => onBinary(e.detail.chunk));
      peer.addEventListener('closed', () => {
        // A refusal is terminal: the desktop is there and reachable, it just
        // won't talk to this device. Saying "waiting for desktop…" would send
        // the user hunting for a problem that isn't the one they have.
        if (state.connection !== CONNECTION.UNPAIRED) push({ connection: CONNECTION.WAITING });
      });
      peer.addEventListener('state', (e) => {
        const ice = e.detail.state;
        // ICE flaps. `disconnected` in particular is routinely transient — a
        // tunnel, a handover, a few lost packets — and it used to be a one-way
        // door: nothing ever moved the badge back, so a link that recovered
        // seconds later still read "reconnecting" for the rest of the session.
        // That is the "it says disconnected but it clearly works" complaint.
        if (ice === 'failed' || ice === 'disconnected') {
          if (admitted) push({ connection: CONNECTION.RECONNECTING });
        } else if ((ice === 'connected' || ice === 'completed') && admitted && peer?.open) {
          push({ connection: CONNECTION.TETHERED, error: null });
        }
      });
      try {
        const sdp = await peer.makeOffer();
        signaling.sendSignal(mkOffer(sdp));
      } catch (err) {
        push({ connection: CONNECTION.ERROR, error: `offer failed: ${err.message}` });
      }
    }
    function onPeerOpen() {
      // The data channel is open, but we're not "tethered" until the host's HELLO
      // proves the desktop is there and this device is verified — stay in the
      // linking state (see the LINK.HELLO handler) so the UI never shows a green
      // "connected" that can't actually take a message.
      push({ connection: CONNECTION.NEGOTIATING, error: null });
      updateNowPlaying();
      if (settings.get('conversationOnConnect') || settings.get('drivingMode')) startConversation();
    }
    // Prove this device to the host: sign `${token}|${nonce}` with our persistent
    // key. The host TOFU-pins the key on first pair and rejects any other later.
    async function sendSignedHello(nonce) {
      const token = tethers.active()?.room || '';
      try {
        const pubKey = await identity.publicKeyJwk();
        const sig = await identity.sign(token, nonce);
        peer?.send(helloGuest({ pubKey, sig }));
      } catch {
        peer?.send(helloGuest());
      }
    }

    // --- tethers: connect to the active one; reconnect on switch ------------
    let currentTetherId = null;
    const refreshTethers = () => {
      state.tethers = tethers.list();
      state.activeTetherId = tethers.activeId;
      // Keep the status-bar chip in sync with the active tether's label, so the
      // desktop's friendly auto-label (agent · dir) replaces the raw room code
      // once HELLO arrives — `change` fires without a reconnect.
      const active = tethers.active();
      state.tetherLabel = active ? active.label : null;
    };
    function connectActive() {
      const t = tethers.active();
      teardownPeer();
      signaling.close();
      refreshTethers();
      currentTetherId = t?.id || null;
      if (t) {
        push({ connection: CONNECTION.CONNECTING, room: t.room, tetherLabel: t.label, agent: null, currentSession: null });
        signaling.connect({ url: t.backendUrl, room: t.room });
      } else {
        push({ connection: CONNECTION.NONE, room: '', tetherLabel: null });
      }
    }
    tethers.addEventListener('change', () => {
      refreshTethers();
      if ((tethers.active()?.id || null) !== currentTetherId) connectActive();
      else notifyNow();
    });

    // Everything the relay says about the *other end's socket* is second-hand
    // news about our link. The relay drops, hibernates, or loses the desktop's
    // socket for a moment and announces a departure and an arrival — while the
    // P2P channel between us carries on, untouched, because it never went
    // through the relay in the first place. Acting on that news tore down a
    // working tether and made the user watch it rebuild.
    //
    // So: ask the link itself. PING/PONG is already in the protocol and the
    // desktop already answers it. If it answers, there is nothing to do.
    const pongWaiters = new Map();
    let pingSeq = 0;
    function probeLink(timeoutMs = 2500) {
      if (!admitted || !peer?.open) return Promise.resolve(false);
      const ts = ++pingSeq;
      if (!peer.send(mkPing(ts))) return Promise.resolve(false);
      return new Promise((resolve) => {
        const timer = setTimeout(() => {
          pongWaiters.delete(ts);
          resolve(false);
        }, timeoutMs);
        pongWaiters.set(ts, () => {
          clearTimeout(timer);
          pongWaiters.delete(ts);
          resolve(true);
        });
      });
    }

    signaling.addEventListener('open', () => {
      if (!admitted || !peer?.open) push({ connection: CONNECTION.CONNECTING });
    });
    signaling.addEventListener('joined', async (e) => {
      const peers = e.detail.peers || [];
      if (peers.includes('host')) {
        hostAnnounced();
      } else if (!(await probeLink())) {
        push({ connection: CONNECTION.WAITING });
      }
    });
    signaling.addEventListener('peer-join', (e) => {
      if (e.detail.role === 'host') hostAnnounced();
    });
    signaling.addEventListener('peer-leave', async () => {
      if (await probeLink()) return; // the desktop's socket left; our tether did not
      teardownPeer();
      push({ connection: CONNECTION.WAITING, agent: null });
    });

    // The desktop turned up on the relay. If our channel still answers, that is
    // the same desktop reconnecting its signaling socket — renegotiating would
    // throw away a link that works. Only a link that has actually gone silent
    // gets rebuilt.
    async function hostAnnounced() {
      if (await probeLink()) return;
      startOffer();
    }
    signaling.addEventListener('signal', (e) => peer && peer.accept(e.detail.data));
    signaling.addEventListener('relay-error', (e) => push({ error: e.detail.message }));
    signaling.addEventListener('close', () => {
      // Signaling is only how the two ends *find* each other. Once the data
      // channel is up the relay can drop, hibernate, or be closed for the night
      // and the tether is completely unaffected — so losing it is not news, and
      // reporting it as "reconnecting" was the single most misleading thing the
      // status bead did. Only say so if the link itself is actually down.
      if (state.connection === CONNECTION.TETHERED && !peer?.open) {
        push({ connection: CONNECTION.RECONNECTING });
      }
    });

    // The host verified this device. This — and only this — is "tethered".
    function onAdmitted() {
      if (admitted) return; // READY can repeat across a re-handshake; the greeting shouldn't
      admitted = true;
      push({ connection: CONNECTION.TETHERED, error: null });
      addMessage('system', `connected to ${state.agent || 'agent'}`, 'status');
      updateNowPlaying();
      flushOutbox(); // send anything composed while we were still linking
    }

    // --- inbound link messages ----------------------------------------------
    function handleLink(msg) {
      switch (msg.t) {
        case LINK.HELLO: {
          // HELLO is the host's challenge, not its blessing. Stay in the linking
          // state and answer it; going green here meant a device that was about
          // to be refused still showed "tethered" and swallowed everything the
          // user typed into it.
          push({ connection: CONNECTION.NEGOTIATING, agent: msg.agent || 'agent', error: null });
          sendSignedHello(msg.nonce); // authenticate this device to the host
          // Give the active tether a friendly auto-label from the desktop.
          const base = (msg.cwd || '').split(/[\\/]/).filter(Boolean).pop();
          if (state.activeTetherId) tethers.setAutoLabel(state.activeTetherId, base ? `${msg.agent} · ${base}` : msg.agent);
          updateNowPlaying();
          // A pre-READY desktop (proto 1) never sends a verdict, so take HELLO as
          // one rather than leaving an older install stuck on "linking…".
          if ((msg.proto || 1) < 2) onAdmitted();
          break;
        }
        case LINK.READY:
          onAdmitted();
          break;
        case LINK.OUTPUT:
          if (!sawOutput) {
            sawOutput = true;
            markRead(); // the agent is replying, so it read our input
            earcon('done'); // the agent has started replying
          }
          armIdle(); // (no-op once the desktop is driving turn-start/turn-end)
          appendAssistant(msg.text);
          speakChunk(msg.text);
          break;
        case LINK.STATUS:
          if (msg.state === 'turn-start') {
            markRead(); // the agent picked up our input
            if (!state.awaitingReply) beginTurn();
            explicitTurn = true; // authoritative turn — disarm the idle heuristic
            clearTimeout(idleTimer);
          } else if (msg.state === 'turn-end') {
            endTurn();
          } else if (msg.state === 'exited') {
            addMessage('system', `agent exited (${msg.code ?? '?'})`, 'status');
            endTurn();
          }
          break;
        case LINK.SESSIONS:
          push({ sessions: msg.sessions || [], currentSession: findSession(msg.sessions, msg.currentId), sessionsOpen: true, sessionsLoading: false });
          break;
        case LINK.SESSION: {
          // On reconnect the desktop re-announces the SAME session — that's a
          // seamless resume, so don't clear the transcript or add noise for it.
          const sameSession = state.currentSession && state.currentSession.id === msg.id;
          markAssistantDone();
          if (!msg.resumed) state.messages = []; // a genuinely fresh conversation
          push({ currentSession: { id: msg.id, title: msg.title }, sessionsOpen: false });
          if (!sameSession) {
            addMessage('system', msg.resumed ? `resumed ${msg.title}` : 'new conversation', 'status');
          }
          updateNowPlaying();
          break;
        }

        // --- front-end control (agent MCP tools) ---
        case LINK.SPEAK:
          tts.speak(msg.text);
          break;
        // Both ends probe the channel before believing the relay that it died,
        // so both ends have to answer. The desktop asking and getting silence is
        // the desktop concluding the phone is gone and rebuilding the tether.
        case LINK.PING:
          peer?.send(mkPong(msg.ts));
          break;
        case LINK.PONG:
          pongWaiters.get(msg.ts)?.();
          break;
        case LINK.STATUSLINE:
          push({ statusLine: msg.text || '' });
          break;
        case LINK.NOTICE:
          // A refusal isn't a toast — it doesn't go away on its own and the user
          // has to do something on the desktop about it, so it takes over the
          // connection state and stays on screen.
          if (msg.code === NOTICE_CODE.UNPAIRED) {
            admitted = false;
            push({ connection: CONNECTION.UNPAIRED, error: msg.text, agent: null });
            if (autoSpeakOn()) tts.speak(msg.text, { remember: false });
          } else {
            showToast(msg.text, msg.level);
          }
          break;
        case LINK.MARKDOWN:
          markAssistantDone();
          addMessage('assistant', msg.markdown, 'markdown', { title: msg.title });
          break;
        case LINK.ASK:
          push({ ask: { id: msg.id, question: msg.question, choices: msg.choices } });
          if (autoSpeakOn()) tts.speak(msg.question, { remember: false });
          break;
        case LINK.FORM:
          push({ form: { id: msg.id, html: msg.html, title: msg.title, submit: msg.submit } });
          if (autoSpeakOn()) tts.speak(msg.title ? `Form: ${msg.title}` : 'The agent sent you a form.', { remember: false });
          break;
        case LINK.ASSET_BEGIN:
          incomingAsset = { ...msg, chunks: [], received: 0 };
          break;
        case LINK.ASSET_END:
          finishAsset(msg.id);
          break;
        default:
          break;
      }
    }

    function onBinary(chunk) {
      if (incomingAsset) {
        incomingAsset.chunks.push(chunk);
        incomingAsset.received += chunk.byteLength || 0;
      }
    }

    function finishAsset(id) {
      const a = incomingAsset;
      incomingAsset = null;
      if (!a || a.id !== id || !a.chunks.length) return;
      const blob = new Blob(a.chunks, { type: a.mime || 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      markAssistantDone();
      addMessage('assistant', (a.meta && a.meta.caption) || a.name || '', a.kind, {
        url,
        name: a.name,
        mime: a.mime,
        autoplay: a.kind === 'audio' && (!a.meta || a.meta.autoplay !== false),
      });
    }

    let toastTimer = null;
    function showToast(text, level = 'info') {
      push({ toast: { text, level } });
      clearTimeout(toastTimer);
      toastTimer = setTimeout(() => push({ toast: null }), 5000);
    }

    const findSession = (list, id) => (list || []).find((s) => s.id === id) || (id ? { id, title: 'current' } : null);

    // Submit a form: stream any files as binary (the desktop saves each to disk),
    // then send the text values. Files never round-trip through the transcript.
    const UPLOAD_CHUNK = 16 * 1024;
    async function submitForm(id, values, files) {
      push({ form: null });
      addMessage('user', 'submitted a form', 'answer');
      try {
        for (const { field, file } of files || []) {
          peer?.send(mkFormFileBegin(id, field, { name: file.name, mime: file.type, size: file.size }));
          const buf = new Uint8Array(await file.arrayBuffer());
          for (let o = 0; o < buf.length; o += UPLOAD_CHUNK) {
            peer?.sendBinary(buf.subarray(o, o + UPLOAD_CHUNK));
          }
          peer?.send(mkFormFileEnd(id, field));
        }
      } catch (err) {
        push({ error: `form upload failed: ${err.message}` });
      }
      peer?.send(mkFormReply(id, values || {}));
    }

    // The phone decides: command or dictation.
    function onTranscript(heard) {
      push({ processing: false });
      const text = meaningfulTranscript(heard);
      if (!text) return; // blank audio / background noise — ignore
      const cmd = parseCommand(text, { leadIn: settings.get('commandLeadIn') });
      if (cmd) {
        addMessage('user', text, 'command');
        runCommand(cmd);
      } else if (state.ask) {
        // A question is pending — this speech is the answer.
        answerAsk(resolveChoice(text, state.ask.choices));
      } else {
        queueOrSend(text, undefined, 'voice'); // transcribed speech
      }
    }

    function answerAsk(answer) {
      if (!state.ask) return;
      peer?.send(mkAskReply(state.ask.id, answer));
      addMessage('user', answer, 'answer');
      push({ ask: null });
    }

    function runCommand(cmd) {
      switch (cmd.name) {
        case CMD.PAUSE_LISTENING:
          mic.pause();
          push({ listening: false });
          break;
        case CMD.RESUME_LISTENING:
          if (!state.conversation) startConversation();
          else {
            mic.resume();
            push({ listening: true });
          }
          break;
        case CMD.STOP_SPEAKING:
          tts.cancel();
          break;
        case CMD.REPEAT:
          tts.repeat();
          break;
        case CMD.START_CONVERSATION:
          startConversation();
          break;
        case CMD.STOP_CONVERSATION:
          stopConversation();
          break;
        case CMD.INTERRUPT_AGENT:
          peer?.send(mkCommand(COMMAND.INTERRUPT));
          break;
        case CMD.FASTER:
          adjustRate(0.15);
          break;
        case CMD.SLOWER:
          adjustRate(-0.15);
          break;
        case CMD.CLEAR:
          state.messages = [];
          notifyNow();
          break;
        case CMD.SESSIONS:
          peer?.send(mkListSessions());
          break;
        case CMD.NEW_SESSION:
          peer?.send(mkConnectSession(null));
          break;
        case CMD.CONNECT_SESSION: {
          const target = state.sessions[(cmd.index || 1) - 1];
          if (target) peer?.send(mkConnectSession(target.id));
          else {
            peer?.send(mkListSessions());
            addMessage('system', `say "${settings.get('commandLeadIn')} sessions" first to see the list`, 'status');
          }
          break;
        }
        case CMD.TETHERS:
          push({ tethersOpen: true });
          break;
        case CMD.SWITCH_TETHER: {
          const t = state.tethers[(cmd.index || 1) - 1];
          if (t) tethers.setActive(t.id);
          else push({ tethersOpen: true });
          break;
        }
        default:
          addMessage('system', `unknown command${cmd.rest ? `: ${cmd.rest}` : ''}`, 'error');
      }
    }

    // --- conversation + manual control --------------------------------------
    async function startConversation() {
      earcons.resume(); // unlock audio for earcons within this user gesture
      // Don't pretend we're listening before the model can actually transcribe:
      // on first use, gate behind a full-screen "preparing voice" overlay until
      // the one-time download finishes. Subsequent starts are instant.
      if (stt.readyState !== 'ready') {
        push({ preparingVoice: true, sttState: 'loading', sttProgress: null, sttBytes: 0, error: null });
        try {
          await stt.ensureReady();
        } catch (err) {
          push({ preparingVoice: false, sttState: 'error', error: `couldn't load the speech model: ${err.message}` });
          return;
        }
        if (!state.preparingVoice) return; // user backed out while it loaded
        push({ preparingVoice: false, sttState: 'ready' });
      }
      try {
        await mic.start();
        if (settings.get('keepAwake') || settings.get('drivingMode')) wake.enable();
        if (settings.get('mediaControls')) media.activate(); // hold session for hw buttons
        push({ conversation: true, listening: true });
        media.setListening(true);
        earcon('listen');
      } catch (err) {
        push({ error: `mic: ${err.message}` });
      }
    }
    function cancelPreparingVoice() {
      push({ preparingVoice: false });
    }
    async function stopConversation() {
      await mic.stop();
      tts.cancel();
      wake.disable();
      media.deactivate();
      push({ conversation: false, listening: false });
      earcon('stop');
    }
    function toggleListening() {
      if (state.listening) {
        mic.pause();
        push({ listening: false });
        media.setListening(false);
        earcon('stop');
      } else {
        mic.resume();
        push({ listening: true });
        media.setListening(true);
        earcon('listen');
      }
    }
    function adjustRate(delta) {
      settings.set('ttsRate', clamp(settings.get('ttsRate') + delta, 0.5, 2.5));
      refreshSettings();
      notifyNow();
    }
    // Lock-screen / car "now playing" card: what you're tethered to.
    function updateNowPlaying() {
      media.update({
        title: state.tetherLabel || state.agent || 'bridle',
        artist: (state.currentSession && state.currentSession.title) || (state.agent ? 'voice agent' : 'tether your agent'),
      });
    }

    // --- UI intents (dispatched Actions emit these on the engineFeed) -------
    function onIntent(e) {
      const it = e.detail || {};
      switch (it.type) {
        case 'send-text': {
          const typed = (it.text || '').trim();
          if (!typed && !state.attachments.length) break;
          if (state.ask) {
            peer?.send(mkAskReply(state.ask.id, typed));
            addMessage('user', typed, 'answer');
            push({ ask: null });
          } else {
            const names = state.attachments.map((a) => a.name).join(', ');
            const display = typed || `📎 ${names}`;
            // Stream the files first so the desktop has them pending BEFORE the
            // text lands (the channel is ordered, but the upload is async).
            if (state.attachments.length && canDeliver()) {
              uploadAttachments().then(() => queueOrSend(display, undefined, 'text')).catch(() => {});
            } else {
              queueOrSend(display, undefined, 'text');
            }
          }
          break;
        }
        case 'attach-files': {
          const added = (it.files || []).map((file) => ({ id: `${nonce}-att-${++attachSeq}`, name: file.name, size: file.size, file }));
          if (added.length) {
            push({ attachments: [...state.attachments, ...added] });
          }
          break;
        }
        case 'remove-attachment':
          push({ attachments: state.attachments.filter((a) => a.id !== it.id) });
          break;
        case 'toggle-conversation':
          state.conversation ? stopConversation() : startConversation();
          break;
        case 'cancel-voice-prep':
          cancelPreparingVoice();
          break;
        case 'toggle-listening':
          toggleListening();
          break;
        case 'stop-speaking':
          tts.cancel();
          break;
        case 'speak-message':
          if (it.text) {
            tts.cancel();
            tts.speak(it.text, { remember: false });
          }
          break;
        case 'repeat':
          tts.repeat();
          break;
        case 'interrupt':
          peer?.send(mkCommand(COMMAND.INTERRUPT));
          break;
        case 'restart':
          peer?.send(mkCommand(COMMAND.RESTART));
          break;
        case 'clear':
          state.messages = [];
          notifyNow();
          break;
        case 'manual-start':
          stt.prewarm();
          mic.startManual().then(() => push({ listening: true })).catch((err) => push({ error: `mic: ${err.message}` }));
          break;
        case 'manual-stop':
          mic.stopManual();
          push({ listening: false });
          break;
        case 'settings-changed':
          refreshSettings();
          notifyNow();
          break;
        case 'set-sheet':
          push({ sheetOpen: !!it.open });
          break;
        case 'list-sessions':
          // Open the picker straight away so the button always responds; request
          // the list if we can, otherwise the sheet explains we're not linked.
          push({ sessionsOpen: true, sessionsLoading: canDeliver() });
          if (canDeliver()) {
            peer.send(mkListSessions());
          }
          break;
        case 'connect-session':
          if (!canDeliver()) {
            showToast('Not linked to your desktop yet — connect first.', 'error');
            break;
          }
          peer.send(mkConnectSession(it.id || null));
          break;
        case 'set-sessions-sheet':
          push({ sessionsOpen: !!it.open });
          break;
        case 'answer-ask':
          if (state.ask) {
            peer?.send(mkAskReply(state.ask.id, it.answer));
            addMessage('user', it.answer, 'answer');
            push({ ask: null });
          }
          break;
        case 'submit-form':
          if (state.form && state.form.id === it.id) {
            submitForm(it.id, it.values, it.files);
          }
          break;
        case 'cancel-form':
          if (state.form && state.form.id === it.id) {
            peer?.send(mkFormReply(it.id, null));
            push({ form: null });
          }
          break;
        case 'switch-tether':
          tethers.setActive(it.id);
          break;
        case 'add-tether':
          if (it.room) tethers.setActive(tethers.add({ room: it.room, backendUrl: it.backendUrl, label: it.label }));
          break;
        case 'remove-tether':
          tethers.remove(it.id);
          break;
        case 'rename-tether':
          tethers.rename(it.id, it.label);
          break;
        case 'set-tethers-sheet':
          push({ tethersOpen: !!it.open });
          break;
        case 'set-details-sheet':
          push({ detailsOpen: !!it.open });
          break;
        case 'set-shortcuts':
          push({ shortcutsOpen: !!it.open });
          break;
        case 'close-sheets':
          if (state.sheetOpen || state.sessionsOpen || state.tethersOpen || state.detailsOpen || state.shortcutsOpen || state.preparingVoice) {
            push({ sheetOpen: false, sessionsOpen: false, tethersOpen: false, detailsOpen: false, shortcutsOpen: false, preparingVoice: false });
          }
          break;
        default:
          break;
      }
    }
    engineFeed.addEventListener('intent', onIntent);

    // Hardware transport controls (headset / Bluetooth / car / lock screen).
    const media = setupMediaSession({
      play: () => { if (!state.conversation) startConversation(); else if (!state.listening) toggleListening(); },
      pause: () => state.listening && toggleListening(),
      toggleMic: () => (state.conversation ? toggleListening() : startConversation()),
      stop: () => tts.cancel(), // barge-in
      previous: () => tts.repeat(),
      next: () => peer?.send(mkCommand(COMMAND.INTERRUPT)), // skip = interrupt the turn
      seekForward: () => adjustRate(0.15), // faster speech
      seekBackward: () => adjustRate(-0.15), // slower speech
      hangup: () => stopConversation(),
    });

    // --- go -----------------------------------------------------------------
    connectActive();
    notifyNow();

    this.kill = () => {
      engineFeed.removeEventListener('intent', onIntent);
      clearTimeout(flushTimer);
      teardownPeer();
      signaling.close();
      mic.stop();
      tts.cancel();
      wake.disable();
    };
  }
}

// --- Actions (thin verbs the UI dispatches) ---------------------------------

const emit = (engineFeed, intent) => engineFeed.dispatchEvent(new CustomEvent('intent', { detail: intent }));

export class SendTextAction extends Action {
  constructor(text) {
    super();
    this.text = text;
  }
  execute(_, { engineFeed }) {
    emit(engineFeed, { type: 'send-text', text: this.text });
  }
}
export class AttachFilesAction extends Action {
  constructor(files) {
    super();
    this.files = files;
  }
  execute(_, { engineFeed }) {
    emit(engineFeed, { type: 'attach-files', files: this.files });
  }
}
export class RemoveAttachmentAction extends Action {
  constructor(id) {
    super();
    this.id = id;
  }
  execute(_, { engineFeed }) {
    emit(engineFeed, { type: 'remove-attachment', id: this.id });
  }
}
class IntentOnly extends Action {
  execute(_, { engineFeed }) {
    emit(engineFeed, this.intent);
  }
}
export class ToggleConversationAction extends IntentOnly {
  intent = { type: 'toggle-conversation' };
}
export class CancelVoicePrepAction extends IntentOnly {
  intent = { type: 'cancel-voice-prep' };
}
export class ToggleListeningAction extends IntentOnly {
  intent = { type: 'toggle-listening' };
}
export class StopSpeakingAction extends IntentOnly {
  intent = { type: 'stop-speaking' };
}
export class SpeakMessageAction extends Action {
  constructor(text) {
    super();
    this.text = text;
  }
  execute(_, { engineFeed }) {
    emit(engineFeed, { type: 'speak-message', text: this.text });
  }
}
export class RepeatAction extends IntentOnly {
  intent = { type: 'repeat' };
}
export class InterruptAgentAction extends IntentOnly {
  intent = { type: 'interrupt' };
}
export class RestartAgentAction extends IntentOnly {
  intent = { type: 'restart' };
}
export class ClearAction extends IntentOnly {
  intent = { type: 'clear' };
}
export class ManualStartAction extends IntentOnly {
  intent = { type: 'manual-start' };
}
export class ManualStopAction extends IntentOnly {
  intent = { type: 'manual-stop' };
}
export class OpenSettingsAction extends IntentOnly {
  intent = { type: 'set-sheet', open: true };
}
export class CloseSettingsAction extends IntentOnly {
  intent = { type: 'set-sheet', open: false };
}
export class ListSessionsAction extends IntentOnly {
  intent = { type: 'list-sessions' };
}
export class NewSessionAction extends Action {
  execute(_, { engineFeed }) {
    emit(engineFeed, { type: 'connect-session', id: null });
  }
}
export class ConnectSessionAction extends Action {
  constructor(id) {
    super();
    this.id = id;
  }
  execute(_, { engineFeed }) {
    emit(engineFeed, { type: 'connect-session', id: this.id });
  }
}
export class CloseSessionsAction extends IntentOnly {
  intent = { type: 'set-sessions-sheet', open: false };
}
export class AnswerAskAction extends Action {
  constructor(answer) {
    super();
    this.answer = answer;
  }
  execute(_, { engineFeed }) {
    emit(engineFeed, { type: 'answer-ask', answer: this.answer });
  }
}
export class SubmitFormAction extends Action {
  constructor({ id, values, files } = {}) {
    super();
    this.id = id;
    this.values = values;
    this.files = files;
  }
  execute(_, { engineFeed }) {
    emit(engineFeed, { type: 'submit-form', id: this.id, values: this.values, files: this.files });
  }
}
export class CancelFormAction extends Action {
  constructor(id) {
    super();
    this.id = id;
  }
  execute(_, { engineFeed }) {
    emit(engineFeed, { type: 'cancel-form', id: this.id });
  }
}

// --- tethers ---
export class OpenTethersAction extends IntentOnly {
  intent = { type: 'set-tethers-sheet', open: true };
}
export class CloseTethersAction extends IntentOnly {
  intent = { type: 'set-tethers-sheet', open: false };
}
export class OpenDetailsAction extends IntentOnly {
  intent = { type: 'set-details-sheet', open: true };
}
export class CloseDetailsAction extends IntentOnly {
  intent = { type: 'set-details-sheet', open: false };
}
export class OpenShortcutsAction extends IntentOnly {
  intent = { type: 'set-shortcuts', open: true };
}
export class CloseShortcutsAction extends IntentOnly {
  intent = { type: 'set-shortcuts', open: false };
}
export class CloseSheetsAction extends IntentOnly {
  intent = { type: 'close-sheets' };
}
export class SwitchTetherAction extends Action {
  constructor(id) {
    super();
    this.id = id;
  }
  execute(_, { engineFeed }) {
    emit(engineFeed, { type: 'switch-tether', id: this.id });
  }
}
export class AddTetherAction extends Action {
  constructor({ room, backendUrl, label } = {}) {
    super();
    this.room = room;
    this.backendUrl = backendUrl;
    this.label = label;
  }
  execute(_, { engineFeed }) {
    emit(engineFeed, { type: 'add-tether', room: this.room, backendUrl: this.backendUrl, label: this.label });
  }
}
export class RemoveTetherAction extends Action {
  constructor(id) {
    super();
    this.id = id;
  }
  execute(_, { engineFeed }) {
    emit(engineFeed, { type: 'remove-tether', id: this.id });
  }
}
export class RenameTetherAction extends Action {
  constructor(id, label) {
    super();
    this.id = id;
    this.label = label;
  }
  execute(_, { engineFeed }) {
    emit(engineFeed, { type: 'rename-tether', id: this.id, label: this.label });
  }
}

export class SetSettingAction extends Action {
  static deps = ['settings'];
  constructor(key, value) {
    super();
    this.key = key;
    this.value = value;
  }
  execute({ settings }, { engineFeed }) {
    settings.set(this.key, this.value);
    emit(engineFeed, { type: 'settings-changed', key: this.key, value: this.value });
  }
}
